import { Supabase } from '../Utils/supabase.config';

/**
 * Create a new report for a post
 * @param {Object} reportData - The report data
 * @param {string} reportData.postId - The ID of the reported post
 * @param {string} reportData.reason - The reason for reporting
 * @param {string} reportData.reporterId - The ID of the user reporting
 * @returns {Promise<Object>} - Success status and data/error message
 */
export const createReport = async (reportData) => {
  try {
    const { data, error } = await Supabase
      .from('reports')
      .insert([
        {
          post_id: parseInt(reportData.postId), // Convert to bigint
          reporter_id: reportData.reporterId,
          reason: reportData.reason,
        }
      ])
      .select();

    if (error) {
      console.error('Error creating report:', error);
      return {
        success: false,
        msg: error.message || 'Failed to create report'
      };
    }

    return {
      success: true,
      data: data[0],
      msg: 'Report submitted successfully'
    };
  } catch (error) {
    console.error('Exception in createReport:', error);
    return {
      success: false,
      msg: 'An unexpected error occurred'
    };
  }
};

/**
 * Check if user has admin or super_admin role
 * @param {string} userId - The user ID to check
 * @returns {Promise<Object>} - Success status and whether user is admin
 */
async function checkAdminRole(userId) {
  try {
    const { data, error } = await Supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .in('role', ['admin', 'super_admin'])
      .single();
    
    if (error) {
      return { success: false, data: false };
    }
    
    return { success: true, data: !!data };
  } catch (error) {
    return { success: false, data: false };
  }
}

/**
 * Get all reports for admin review
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (optional)
 * @param {number} options.limit - Number of reports to fetch (default: 50)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {string} options.userId - The ID of the user requesting reports (required for role check)
 * @returns {Promise<Object>} - Success status and reports data
 */
export const getReports = async (options = {}) => {
  try {
    const { status, limit = 50, offset = 0, userId } = options;
    
    // Check if userId is provided
    if (!userId) {
      return {
        success: false,
        msg: 'User ID is required to access reports'
      };
    }
    
    // Verify user has admin role
    const roleCheck = await checkAdminRole(userId);
    if (!roleCheck.success || !roleCheck.data) {
      return {
        success: false,
        msg: 'Access denied. Only administrators can view reports.'
      };
    }
    
    let query = Supabase
      .from('reports')
      .select(`
        *,
        posts:post_id (
          id,
          body,
          file,
          created_at,
          userId
        )
      `)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching reports:', error);
      return {
        success: false,
        msg: error.message || 'Failed to fetch reports'
      };
    }

    return {
      success: true,
      data: data || [],
      msg: 'Reports fetched successfully'
    };
  } catch (error) {
    console.error('Exception in getReports:', error);
    return {
      success: false,
      msg: 'An unexpected error occurred'
    };
  }
};

/**
 * Update report status (for admin actions)
 * @param {string} reportId - The ID of the report to update
 * @param {Object} updateData - The data to update
 * @param {string} updateData.status - New status
 * @param {string} updateData.adminNotes - Admin notes
 * @param {string} updateData.reviewedBy - ID of the admin reviewing
 * @returns {Promise<Object>} - Success status and updated data
 */
export const updateReportStatus = async (reportId, updateData) => {
  try {
    const { status, adminNotes, reviewedBy } = updateData;
    
    // Check if reviewedBy is provided
    if (!reviewedBy) {
      return {
        success: false,
        msg: 'Admin user ID is required to update report status'
      };
    }
    
    // Verify user has admin role
    const roleCheck = await checkAdminRole(reviewedBy);
    if (!roleCheck.success || !roleCheck.data) {
      return {
        success: false,
        msg: 'Access denied. Only administrators can update report status.'
      };
    }
    
    const { data, error } = await Supabase
      .from('reports')
      .update({
        status: status,
        admin_notes: adminNotes,
        reviewed_by: reviewedBy,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', reportId)
      .select();

    if (error) {
      console.error('Error updating report:', error);
      return {
        success: false,
        msg: error.message || 'Failed to update report'
      };
    }

    return {
      success: true,
      data: data[0],
      msg: 'Report status updated successfully'
    };
  } catch (error) {
    console.error('Exception in updateReportStatus:', error);
    return {
      success: false,
      msg: 'An unexpected error occurred'
    };
  }
};

/**
 * Get report statistics for admin dashboard
 * @param {string} userId - The ID of the user requesting statistics (required for role check)
 * @returns {Promise<Object>} - Success status and statistics data
 */
export const getReportStats = async (userId) => {
  try {
    // Check if userId is provided
    if (!userId) {
      return {
        success: false,
        msg: 'User ID is required to access report statistics'
      };
    }
    
    // Verify user has admin role
    const roleCheck = await checkAdminRole(userId);
    if (!roleCheck.success || !roleCheck.data) {
      return {
        success: false,
        msg: 'Access denied. Only administrators can view report statistics.'
      };
    }
    
    const { data, error } = await Supabase
      .rpc('get_report_stats');

    if (error) {
      console.error('Error fetching report stats:', error);
      return {
        success: false,
        msg: error.message || 'Failed to fetch report statistics'
      };
    }

    return {
      success: true,
      data: data || {},
      msg: 'Report statistics fetched successfully'
    };
  } catch (error) {
    console.error('Exception in getReportStats:', error);
    return {
      success: false,
      msg: 'An unexpected error occurred'
    };
  }
};

/**
 * Check if a user has already reported a specific post
 * @param {string} postId - The ID of the post
 * @param {string} userId - The ID of the user
 * @returns {Promise<Object>} - Success status and whether already reported
 */
export const checkIfAlreadyReported = async (postId, userId) => {
  try {
    const { data, error } = await Supabase
      .from('reports')
      .select('id')
      .eq('post_id', parseInt(postId)) // Convert to bigint
      .eq('reporter_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error checking report status:', error);
      return {
        success: false,
        msg: error.message || 'Failed to check report status'
      };
    }

    return {
      success: true,
      alreadyReported: !!data,
      data: data || null
    };
  } catch (error) {
    console.error('Exception in checkIfAlreadyReported:', error);
    return {
      success: false,
      msg: 'An unexpected error occurred'
    };
  }
};

/**
 * Hide a post when a report is accepted
 * @param {string} postId - The ID of the post to hide
 * @param {string} adminUserId - The ID of the admin user performing the action (required for role check)
 * @returns {Promise<Object>} - Success status and data/error message
 */
export const hidePost = async (postId, adminUserId) => {
  try {
    // Check if adminUserId is provided
    if (!adminUserId) {
      return {
        success: false,
        msg: 'Admin user ID is required to hide posts'
      };
    }
    
    // Verify user has admin role
    const roleCheck = await checkAdminRole(adminUserId);
    if (!roleCheck.success || !roleCheck.data) {
      return {
        success: false,
        msg: 'Access denied. Only administrators can hide posts.'
      };
    }
    
    // Add a 'hidden' field to the posts table or use a status field
    const { data, error } = await Supabase
      .from('posts')
      .update({ 
        hidden: true,
        hidden_at: new Date().toISOString()
      })
      .eq('id', parseInt(postId))
      .select();

    if (error) {
      console.error('Error hiding post:', error);
      return {
        success: false,
        msg: error.message || 'Failed to hide post'
      };
    }

    return {
      success: true,
      data: data[0],
      msg: 'Post hidden successfully'
    };
  } catch (error) {
    console.error('Exception in hidePost:', error);
    return {
      success: false,
      msg: 'An unexpected error occurred'
    };
  }
};
